<template>
  <div class="map-container" ref="mapContainer"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import L from 'leaflet'
import 'leaflet-routing-machine'
import type { Stop, StopType } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { reverseGeocodePlace, type ReverseGeocodingPlace } from '@/services/geocoding'
import { searchFeaturedAlongRoute, searchNearbyPOIs, searchSettlementLabels, formatDistance, type POI } from '@/services/poi'

interface Props {
  stops?: Stop[]
  center?: [number, number]
  zoom?: number
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  stops: () => [],
  center: () => [47.5, 19.0], // Budapest, Europe center
  zoom: 6,
  readonly: false
})

const settingsStore = useSettingsStore()
const { t } = useI18n()
const { mapLanguage, theme } = storeToRefs(settingsStore)

const emit = defineEmits<{
  mapClick: [event: { lat: number; lng: number }]
  stopClick: [stop: Stop]
  poiAddStop: [stopData: Partial<Stop>]
}>()

const mapContainer = ref<HTMLElement>()
let map: L.Map | null = null
let markers: L.Marker[] = []
let poiMarkers: L.Marker[] = []
let settlementLabelMarkers: L.Marker[] = []
let routingControl: any = null
let lastStopsSignature: string | null = null
let poiLoadTimeout: number | null = null
let routePoiLoadTimeout: number | null = null
let poiLoadRequestId = 0
let routePoiLoadRequestId = 0
let mapClickPopupRequestId = 0
let settlementLabelLoadRequestId = 0
let visiblePoiResults: POI[] = []
let routePoiResults: POI[] = []
let settlementLabelResults: POI[] = []
let routeCoordinates: { lat: number; lng: number }[] = []
let resizeObserver: ResizeObserver | null = null
let tileLayer: L.TileLayer | null = null
let tileLabelLayer: L.TileLayer | null = null
let isUnmounted = false

type MapOptionsWithTap = L.MapOptions & { tap?: boolean }

const stopTypeIcons: Record<StopType, string> = {
  city: '🏙️',
  gas_station: '⛽',
  ev_charging: '⚡',
  rest_area: '🚻',
  restaurant: '🍽️',
  fast_food: '🍔',
  cafe: '☕',
  hotel: '🏨',
  motel: '🛏️',
  camping: '⛺',
  attraction: '🎯',
  museum: '🏛️',
  park: '🌳',
  viewpoint: '🏔️',
  shopping: '🛍️',
  parking: '🅿️',
  other: '📍'
}

onMounted(() => {
  console.log('🗺️ MapView onMounted called')
  initializeMap()
  setupResizeObserver()
  console.log('🗺️ MapView initialization complete')
})

onUnmounted(() => {
  console.log('🗺️ MapView onUnmounted - cleaning up...')
  isUnmounted = true

  if (poiLoadTimeout !== null) {
    window.clearTimeout(poiLoadTimeout)
    poiLoadTimeout = null
  }

  if (routePoiLoadTimeout !== null) {
    window.clearTimeout(routePoiLoadTimeout)
    routePoiLoadTimeout = null
  }
  
  if (resizeObserver && mapContainer.value) {
    resizeObserver.unobserve(mapContainer.value)
    resizeObserver.disconnect()
  }
  
  // Clean up routing control first
  removeRoutingControl()
  
  // Clean up markers
  if (map && markers.length > 0) {
    markers.forEach(marker => {
      try {
        map!.removeLayer(marker)
      } catch (error) {
        console.warn('Error removing marker:', error)
      }
    })
    markers = []
  }

  clearPoiMarkers()
  clearSettlementLabelMarkers()
  
  // Clean up map
  if (map) {
    try {
      map.remove()
    } catch (error) {
      console.warn('Error removing map:', error)
    } finally {
      map = null
    }
  }
  
  console.log('🗺️ MapView cleanup complete')
})

watch(() => props.stops, (newStops, oldStops) => {
  if (!map) return // Guard: only update if map exists

  const stopsSignature = getStopsSignature(newStops)
  if (stopsSignature === lastStopsSignature) {
    return
  }

  lastStopsSignature = stopsSignature
  
  console.log('🗺️ Stops changed:', oldStops?.length, '→', newStops?.length)
  
  updateMarkers(newStops)
  updateRoute(newStops)
  schedulePoiRefresh()
}, { deep: true, immediate: true })

// Watch for map language changes
watch(mapLanguage, (newLang) => {
  console.log('🗺️ Map language changed to:', newLang)
  updateTileLayer()
  renderPoiMarkers(getMergedPoiResults())
  renderSettlementLabelMarkers(settlementLabelResults)
})

// Watch for theme changes
watch(theme, (newTheme) => {
  console.log('🗺️ Theme changed to:', newTheme)
  updateTileLayer()
})

function isDarkMode(): boolean {
  const currentTheme = theme.value
  if (currentTheme === 'dark') return true
  if (currentTheme === 'light') return false
  // For 'auto', check system preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getTileLayerUrl() {
  const darkMode = isDarkMode()

  return darkMode
    ? 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/voyager_nolabels/{z}/{x}/{y}{r}.png'
}

function getTileLabelLayerUrl() {
  const darkMode = isDarkMode()

  return darkMode
    ? 'https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/voyager_only_labels/{z}/{x}/{y}{r}.png'
}

function updateTileLayer() {
  if (!map) return

  // Remove old tile layer
  if (tileLayer) {
    map.removeLayer(tileLayer)
  }

  if (tileLabelLayer) {
    map.removeLayer(tileLabelLayer)
  }

  // Add new tile layer with appropriate language and theme
  const url = getTileLayerUrl()
  const labelUrl = getTileLabelLayerUrl()
  const darkMode = isDarkMode()
  
  tileLayer = L.tileLayer(url, {
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors | © CARTO'
  }).addTo(map)

  tileLabelLayer = L.tileLayer(labelUrl, {
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors | © CARTO',
    opacity: darkMode ? 0.92 : 0.9
  }).addTo(map)

  console.log('🗺️ Tile layer updated - Language:', mapLanguage.value, 'Dark mode:', darkMode)
}

function initializeMap() {
  if (!mapContainer.value) return

  console.log('🗺️ Initializing map...')
  console.log('🗺️ Container dimensions:', {
    width: mapContainer.value.offsetWidth,
    height: mapContainer.value.offsetHeight,
    clientWidth: mapContainer.value.clientWidth,
    clientHeight: mapContainer.value.clientHeight
  })

  const mapOptions: MapOptionsWithTap = {
    zoomControl: true,
    attributionControl: true,
    keyboard: false, // Disable keyboard navigation to prevent focus issues
    tap: false // Disable tap handler on mobile
  }

  map = L.map(mapContainer.value, mapOptions).setView(props.center, props.zoom)

  console.log('🗺️ Map created')

  // Add tile layer with current language and theme setting
  const url = getTileLayerUrl()
  const labelUrl = getTileLabelLayerUrl()
  const darkMode = isDarkMode()
  
  tileLayer = L.tileLayer(url, {
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors | © CARTO'
  }).addTo(map)

  tileLabelLayer = L.tileLayer(labelUrl, {
    maxZoom: 19,
    attribution: '© OpenStreetMap contributors | © CARTO',
    opacity: darkMode ? 0.92 : 0.9
  }).addTo(map)

  console.log('🗺️ Tile layer added - Language:', mapLanguage.value, 'Dark mode:', darkMode)

  // Force map to recalculate size after a short delay
  setTimeout(() => {
    if (map) {
      console.log('🗺️ Invalidating map size...')
      map.invalidateSize()
      console.log('🗺️ Map size invalidated. New size:', map.getSize())
    }
  }, 100)

  // Another check after 500ms
  setTimeout(() => {
    if (map) {
      console.log('🗺️ Second size invalidation...')
      map.invalidateSize()
    }
  }, 500)

  // Handle map clicks
  map.on('click', (e: L.LeafletMouseEvent) => {
    void handleMapLocationClick(e.latlng.lat, e.latlng.lng)
  })

  map.on('moveend', schedulePoiRefresh)

  // Prevent map from stealing focus
  if (mapContainer.value) {
    mapContainer.value.setAttribute('tabindex', '-1')
  }

  // Initial markers and route
  updateMarkers(props.stops)
  updateRoute(props.stops)
  schedulePoiRefresh()
}

function setupResizeObserver() {
  if (!mapContainer.value) return

  console.log('🗺️ Setting up ResizeObserver...')
  
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      console.log('🗺️ Container resized:', {
        width: entry.contentRect.width,
        height: entry.contentRect.height
      })
      
      if (map && entry.contentRect.height > 0 && entry.contentRect.width > 0) {
        // Delay to ensure CSS is applied
        setTimeout(() => {
          if (map) {
            console.log('🗺️ Invalidating size due to resize...')
            map.invalidateSize()
          }
        }, 50)
      }
    }
  })

  resizeObserver.observe(mapContainer.value)
  console.log('🗺️ ResizeObserver setup complete')
}

async function handleMapLocationClick(lat: number, lng: number) {
  if (!map) return

  if (props.readonly) {
    emit('mapClick', { lat, lng })
    return
  }

  const requestId = ++mapClickPopupRequestId
  const popup = L.popup({ closeButton: true, autoClose: true, closeOnClick: true })
    .setLatLng([lat, lng])
    .setContent('<div style="min-width:200px;padding:4px 2px;font-size:13px;color:#475569;">Hely betoltese...</div>')
    .openOn(map)

  const place = await reverseGeocodePlace(lat, lng)

  if (!map || isUnmounted || requestId !== mapClickPopupRequestId) {
    return
  }

  popup.setContent(createMapLocationPopupContent(place, () => {
    emit('poiAddStop', buildStopFromMapLocation(place))
    popup.remove()
  }))
}

function updateMarkers(stops: Stop[]) {
  if (!map) return

  // Clear existing markers
  markers.forEach(marker => map!.removeLayer(marker))
  markers = []

  // Add new markers
  stops.forEach((stop, index) => {
    const icon = L.divIcon({
      html: `
        <div class="flex items-center justify-center w-8 h-8 bg-primary-600 text-white rounded-full border-2 border-white shadow-lg">
          <span class="text-xs font-bold">${index + 1}</span>
        </div>
      `,
      className: 'custom-stop-marker',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })

    const marker = L.marker([stop.lat, stop.lng], { icon })
      .addTo(map!)
      .bindPopup(`
        <div class="p-2">
          <div class="flex items-center gap-2 mb-1">
            <span class="text-lg">${stopTypeIcons[stop.type]}</span>
            <h3 class="font-semibold">${stop.name}</h3>
          </div>
          <p class="text-sm text-gray-600">${stop.address}</p>
          <p class="text-xs text-gray-500 mt-1">Duration: ${stop.duration} min</p>
        </div>
      `)

    marker.on('click', () => emit('stopClick', stop))
    markers.push(marker)
  })

  // Fit map to markers if we have stops
  if (stops.length > 0) {
    const group = new L.FeatureGroup(markers)
    map.fitBounds(group.getBounds().pad(0.1))
  }
}

function updateRoute(stops: Stop[]) {
  if (!map || isUnmounted) return

  // Add route if we have at least 2 stops
  if (stops.length >= 2) {
    const waypoints = stops
      .sort((a, b) => a.order - b.order)
      .map(stop => L.latLng(stop.lat, stop.lng))

    routeCoordinates = []
    routePoiResults = []
    renderPoiMarkers(getMergedPoiResults())

    if (routingControl) {
      try {
        routingControl.setWaypoints(waypoints)

        setTimeout(() => {
          if (map && !isUnmounted && waypoints.length >= 2) {
            const bounds = L.latLngBounds(waypoints)
            map.fitBounds(bounds, { padding: [50, 50] })
          }
        }, 300)

        return
      } catch (error) {
        console.warn('Error updating routing control waypoints:', error)
        removeRoutingControl()
      }
    }

    // Create routing control with error handling
    try {
      routingControl = (L as any).Routing.control({
        waypoints,
        routeWhileDragging: false,
        addWaypoints: false,
        createMarker: () => null, // Don't create default markers
        lineOptions: {
          styles: [
            {
              color: '#3b82f6',
              weight: 4,
              opacity: 0.8
            }
          ]
        },
        show: false // Don't show instructions panel
      })

      // Add event listeners to handle errors
      routingControl.on('routingstart', () => {
        if (isUnmounted) {
          console.warn('Component unmounted during routing')
        }
      })

      routingControl.on('routesfound', (event: any) => {
        if (isUnmounted) return

        const route = event?.routes?.[0]
        routeCoordinates = route?.coordinates?.map((coordinate: any) => ({
          lat: coordinate.lat,
          lng: coordinate.lng
        })) || []
        scheduleRoutePoiRefresh()
      })

      routingControl.on('routingerror', (e: any) => {
        if (!isUnmounted) {
          console.warn('Routing error:', e)
        }
        routeCoordinates = []
        scheduleRoutePoiRefresh()
      })

      // Add to map only if map still exists and not unmounted
      if (map && !isUnmounted) {
        routingControl.addTo(map)

        // Hide the routing instructions panel
        const routingContainer = routingControl.getContainer()
        if (routingContainer) {
          routingContainer.style.display = 'none'
        }

        // Fit map to show all waypoints with padding
        // Wait a bit for the route to be calculated
        setTimeout(() => {
          if (map && !isUnmounted && waypoints.length >= 2) {
            const bounds = L.latLngBounds(waypoints)
            map.fitBounds(bounds, { padding: [50, 50] })
          }
        }, 500)
      }
    } catch (error) {
      if (!isUnmounted) {
        console.warn('Error creating routing control:', error)
      }
      removeRoutingControl()
    }
  } else {
    routeCoordinates = []
    routePoiResults = []
    removeRoutingControl()
    renderPoiMarkers(getMergedPoiResults())
  }
}

function removeRoutingControl() {
  if (!routingControl) return

  try {
    if (routingControl._map) {
      routingControl.remove()
    }
  } catch (error) {
    console.warn('Error removing routing control:', error)
  } finally {
    routingControl = null
  }
}

function schedulePoiRefresh() {
  if (!map || isUnmounted) return

  if (poiLoadTimeout !== null) {
    window.clearTimeout(poiLoadTimeout)
  }

  poiLoadTimeout = window.setTimeout(() => {
    poiLoadTimeout = null
    void refreshVisiblePOIs()
    void refreshSettlementLabels()
  }, 400)
}

function scheduleRoutePoiRefresh() {
  if (!map || isUnmounted) return

  if (routePoiLoadTimeout !== null) {
    window.clearTimeout(routePoiLoadTimeout)
  }

  routePoiLoadTimeout = window.setTimeout(() => {
    routePoiLoadTimeout = null
    void refreshRoutePOIs()
  }, 500)
}

async function refreshVisiblePOIs() {
  if (!map || isUnmounted) return

  const zoom = map.getZoom()
  const hasActiveRoute = routeCoordinates.length >= 2 || props.stops.length >= 2
  const shouldLoadPOIs = !hasActiveRoute && zoom >= 10

  if (!shouldLoadPOIs) {
    visiblePoiResults = []
    renderPoiMarkers(getMergedPoiResults())
    return
  }

  const requestId = ++poiLoadRequestId
  const bounds = map.getBounds()
  const center = bounds.getCenter()
  const radius = Math.min(Math.max(center.distanceTo(bounds.getNorthEast()), 2500), 20000)

  try {
    const pois = await searchNearbyPOIs({
      lat: center.lat,
      lng: center.lng,
      radius: Math.round(radius),
      limit: 18
    })

    if (requestId !== poiLoadRequestId || isUnmounted || !map) return

    visiblePoiResults = pois.filter(poi => !isNearExistingStop(poi))
    renderPoiMarkers(getMergedPoiResults())
  } catch (error) {
    if (requestId !== poiLoadRequestId || isUnmounted) return
    console.warn('Error loading POIs for map view:', error)
    visiblePoiResults = []
    renderPoiMarkers(getMergedPoiResults())
  }
}

async function refreshSettlementLabels() {
  if (!map || isUnmounted) return

  const zoom = map.getZoom()
  if (zoom < 5) {
    settlementLabelResults = []
    renderSettlementLabelMarkers(settlementLabelResults)
    return
  }

  const requestId = ++settlementLabelLoadRequestId
  const bounds = map.getBounds()
  const center = bounds.getCenter()
  const radius = Math.min(
    Math.max(center.distanceTo(bounds.getNorthEast()), zoom >= 10 ? 6000 : 20000),
    zoom >= 11 ? 35000 : zoom >= 8 ? 90000 : 240000
  )

  try {
    const pois = await searchSettlementLabels({
      lat: center.lat,
      lng: center.lng,
      radius: Math.round(radius),
      zoom,
      limit: zoom >= 11 ? 20 : zoom >= 8 ? 14 : 8
    })

    if (requestId !== settlementLabelLoadRequestId || isUnmounted || !map) return

    const seenNames = new Set<string>()

    settlementLabelResults = pois
      .filter(poi => isSettlementPOI(poi) && !isNearExistingStop(poi))
      .sort((a, b) => {
        const priorityDelta = getSettlementLabelPriority(a) - getSettlementLabelPriority(b)
        if (priorityDelta !== 0) return priorityDelta
        return (a.distance || 0) - (b.distance || 0)
      })
      .filter(poi => {
        const key = `${getPOIDisplayName(poi).trim().toLowerCase()}:${poi.type}`
        if (!key || seenNames.has(key)) {
          return false
        }

        seenNames.add(key)
        return true
      })
      .slice(0, zoom >= 11 ? 14 : zoom >= 8 ? 10 : 5)

    renderSettlementLabelMarkers(settlementLabelResults)
  } catch (error) {
    if (requestId !== settlementLabelLoadRequestId || isUnmounted) return

    console.warn('Error loading settlement labels for map view:', error)
    settlementLabelResults = []
    renderSettlementLabelMarkers(settlementLabelResults)
  }
}

async function refreshRoutePOIs() {
  if (!map || isUnmounted) return

  const routePath = getRoutePath()
  if (routePath.length < 2) {
    routePoiResults = []
    renderPoiMarkers(getMergedPoiResults())
    return
  }

  const requestId = ++routePoiLoadRequestId

  try {
    const pois = await searchFeaturedAlongRoute(routePath, 7000)

    if (requestId !== routePoiLoadRequestId || isUnmounted || !map) return

    routePoiResults = pois.filter(poi => !isNearExistingStop(poi))
    renderPoiMarkers(getMergedPoiResults())
  } catch (error) {
    if (requestId !== routePoiLoadRequestId || isUnmounted) return

    console.warn('Error loading route POIs for map view:', error)
    routePoiResults = []
    renderPoiMarkers(getMergedPoiResults())
  }
}

function renderPoiMarkers(pois: POI[]) {
  clearPoiMarkers()
  if (!map || pois.length === 0) return

  pois.forEach(poi => {
    const isFeatured = isFeaturedPOI(poi)
    const marker = L.marker([poi.lat, poi.lng], {
      zIndexOffset: isFeatured ? 2000 : isHighwayRelatedPOI(poi) ? 1500 : 500,
      riseOnHover: true,
      icon: L.divIcon({
        html: createPOIMarkerHtml(poi),
        className: 'custom-poi-marker',
        iconSize: isFeatured ? [96, 84] : isHighwayRelatedPOI(poi) ? [56, 56] : [34, 34],
        iconAnchor: isFeatured ? [48, 58] : isHighwayRelatedPOI(poi) ? [28, 28] : [17, 17],
        popupAnchor: [0, -16]
      })
    }).addTo(map)

    const popupContent = createPOIPopupContent(poi, () => {
      emit('poiAddStop', buildStopFromPOI(poi))
      marker.closePopup()
    })

    marker.bindPopup(popupContent, { closeButton: true })

    if (isFeatured) {
      marker.bindTooltip(getPOITooltipText(poi), {
        permanent: true,
        direction: 'top',
        offset: [0, -24],
        opacity: 1,
        className: 'poi-label-tooltip',
        sticky: false
      })
    }

    poiMarkers.push(marker)
  })
}

function clearPoiMarkers() {
  if (!map || poiMarkers.length === 0) {
    poiMarkers = []
    return
  }

  poiMarkers.forEach(marker => {
    try {
      map!.removeLayer(marker)
    } catch (error) {
      console.warn('Error removing POI marker:', error)
    }
  })

  poiMarkers = []
}

function renderSettlementLabelMarkers(pois: POI[]) {
  clearSettlementLabelMarkers()
  if (!map || pois.length === 0) return

  pois.forEach(poi => {
    const marker = L.marker([poi.lat, poi.lng], {
      zIndexOffset: 1100,
      riseOnHover: true,
      icon: L.divIcon({
        html: createSettlementLabelMarkerHtml(poi),
        className: 'settlement-label-marker',
        iconSize: [1, 1],
        iconAnchor: [0, 0],
        popupAnchor: [0, -18]
      })
    }).addTo(map)

    const popupContent = createPOIPopupContent(poi, () => {
      emit('poiAddStop', buildStopFromPOI(poi))
      marker.closePopup()
    })

    marker.bindPopup(popupContent, { closeButton: true })
    settlementLabelMarkers.push(marker)
  })
}

function clearSettlementLabelMarkers() {
  if (!map || settlementLabelMarkers.length === 0) {
    settlementLabelMarkers = []
    return
  }

  settlementLabelMarkers.forEach(marker => {
    try {
      map!.removeLayer(marker)
    } catch (error) {
      console.warn('Error removing settlement label marker:', error)
    }
  })

  settlementLabelMarkers = []
}

function getMergedPoiResults(): POI[] {
  const merged = new Map<string, POI>()

  for (const poi of visiblePoiResults) {
    merged.set(poi.id, poi)
  }

  for (const poi of routePoiResults) {
    merged.set(poi.id, poi)
  }

  return Array.from(merged.values())
}

function createPOIMarkerHtml(poi: POI): string {
  const icon = getPOIIcon(poi)
  const background = getPOIMarkerColor(poi)
  const isHighway = isHighwayRelatedPOI(poi)
  const isFeatured = isFeaturedPOI(poi)
  const label = escapeHtml(getPOITooltipText(poi))

  if (isFeatured) {
    return `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        pointer-events: none;
      ">
        <div style="
          width: 56px;
          height: 56px;
          border-radius: 9999px;
          background: ${background};
          border: 4px solid rgba(255, 255, 255, 0.98);
          box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.12), 0 12px 24px rgba(15, 23, 42, 0.28);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          line-height: 1;
          transform: translateY(1px);
        ">${icon}</div>
        <div style="
          max-width: 160px;
          padding: 4px 8px;
          border-radius: 9999px;
          background: rgba(15, 23, 42, 0.92);
          color: #ffffff;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.02em;
          line-height: 1.1;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          box-shadow: 0 8px 16px rgba(15, 23, 42, 0.22);
        ">${label}</div>
      </div>
    `
  }

  return `
    <div style="
      width: ${isHighway ? 48 : 34}px;
      height: ${isHighway ? 48 : 34}px;
      border-radius: 9999px;
      background: ${background};
      border: ${isHighway ? 4 : 2}px solid rgba(255, 255, 255, 0.98);
      box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.10), 0 10px 22px rgba(15, 23, 42, ${isHighway ? 0.34 : 0.25});
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: ${isHighway ? 20 : 16}px;
      line-height: 1;
      position: relative;
    ">${icon}</div>
  `
}

function createSettlementLabelMarkerHtml(poi: POI): string {
  const icon = getPOIIcon(poi)
  const name = escapeHtml(getPOIDisplayName(poi))

  return `
    <div style="
      transform: translate(-50%, -50%);
      display: inline-flex;
      align-items: center;
      gap: 6px;
      max-width: 180px;
      padding: 6px 10px;
      border-radius: 9999px;
      background: rgba(255, 255, 255, 0.94);
      border: 1px solid rgba(148, 163, 184, 0.45);
      color: #0f172a;
      box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
      font-size: 12px;
      font-weight: 800;
      line-height: 1;
      white-space: nowrap;
    ">
      <span style="font-size: 14px; line-height: 1; flex-shrink: 0;">${icon}</span>
      <span style="overflow: hidden; text-overflow: ellipsis;">${name}</span>
    </div>
  `
}

function createPOIPopupContent(poi: POI, onAdd: () => void): HTMLElement {
  const container = document.createElement('div')
  container.style.minWidth = '220px'
  container.style.maxWidth = '260px'

  const titleRow = document.createElement('div')
  titleRow.style.display = 'flex'
  titleRow.style.alignItems = 'flex-start'
  titleRow.style.gap = '8px'

  const icon = document.createElement('div')
  icon.textContent = getPOIIcon(poi)
  icon.style.fontSize = '20px'
  icon.style.lineHeight = '1'
  icon.style.flexShrink = '0'

  const textWrap = document.createElement('div')
  textWrap.style.minWidth = '0'
  textWrap.style.flex = '1'

  const title = document.createElement('div')
  title.textContent = getPOIDisplayName(poi)
  title.style.fontWeight = '700'
  title.style.fontSize = '14px'
  title.style.color = '#0f172a'

  const subtitle = document.createElement('div')
  subtitle.textContent = poi.address || getPOILabel(poi)
  subtitle.style.marginTop = '4px'
  subtitle.style.fontSize = '12px'
  subtitle.style.color = '#475569'

  textWrap.appendChild(title)
  textWrap.appendChild(subtitle)
  titleRow.appendChild(icon)
  titleRow.appendChild(textWrap)
  container.appendChild(titleRow)

  if (poi.distance !== undefined) {
    const distance = document.createElement('div')
    distance.textContent = `Távolság: ${formatDistance(poi.distance)}`
    distance.style.marginTop = '8px'
    distance.style.fontSize = '12px'
    distance.style.color = '#64748b'
    container.appendChild(distance)
  }

  if (!props.readonly) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = `➕ ${t('addStop')}`
    button.style.marginTop = '12px'
    button.style.width = '100%'
    button.style.border = 'none'
    button.style.borderRadius = '10px'
    button.style.padding = '8px 12px'
    button.style.background = '#3b82f6'
    button.style.color = '#ffffff'
    button.style.fontSize = '13px'
    button.style.fontWeight = '700'
    button.style.cursor = 'pointer'

    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      onAdd()
    })

    container.appendChild(button)
  }

  return container
}

function createMapLocationPopupContent(place: ReverseGeocodingPlace, onAdd: () => void): HTMLElement {
  const container = document.createElement('div')
  container.style.minWidth = '220px'
  container.style.maxWidth = '260px'

  const titleRow = document.createElement('div')
  titleRow.style.display = 'flex'
  titleRow.style.alignItems = 'flex-start'
  titleRow.style.gap = '8px'

  const icon = document.createElement('div')
  icon.textContent = getMapLocationIcon(place)
  icon.style.fontSize = '20px'
  icon.style.lineHeight = '1'
  icon.style.flexShrink = '0'

  const textWrap = document.createElement('div')
  textWrap.style.minWidth = '0'
  textWrap.style.flex = '1'

  const title = document.createElement('div')
  title.textContent = place.name || 'Kivalasztott hely'
  title.style.fontWeight = '700'
  title.style.fontSize = '14px'
  title.style.color = '#0f172a'

  const subtitle = document.createElement('div')
  subtitle.textContent = place.address || place.name || 'Kivalasztott hely'
  subtitle.style.marginTop = '4px'
  subtitle.style.fontSize = '12px'
  subtitle.style.color = '#475569'

  textWrap.appendChild(title)
  textWrap.appendChild(subtitle)
  titleRow.appendChild(icon)
  titleRow.appendChild(textWrap)
  container.appendChild(titleRow)

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = `➕ ${t('addStop')}`
  button.style.marginTop = '12px'
  button.style.width = '100%'
  button.style.border = 'none'
  button.style.borderRadius = '10px'
  button.style.padding = '8px 12px'
  button.style.background = '#2563eb'
  button.style.color = '#ffffff'
  button.style.fontSize = '13px'
  button.style.fontWeight = '700'
  button.style.cursor = 'pointer'

  button.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    onAdd()
  })

  container.appendChild(button)

  return container
}

function buildStopFromPOI(poi: POI): Partial<Stop> {
  return {
    name: getPOIDisplayName(poi),
    address: poi.address || getPOIDisplayName(poi),
    type: normalizePOIStopType(poi),
    lat: poi.lat,
    lng: poi.lng,
    duration: 60,
    order: props.stops.length,
    notes: buildPOINotes(poi)
  }
}

function buildStopFromMapLocation(place: ReverseGeocodingPlace): Partial<Stop> {
  return {
    name: place.name || place.city || 'Kivalasztott hely',
    address: place.address || place.name || 'Kivalasztott hely',
    type: isSettlementType(place.type) ? 'city' : 'other',
    lat: place.lat,
    lng: place.lng,
    duration: 60,
    order: props.stops.length
  }
}

function buildPOINotes(poi: POI): string {
  const parts: string[] = []

  if (poi.phone) parts.push(`📞 ${poi.phone}`)
  if (poi.website) parts.push(`🌐 ${poi.website}`)
  if (poi.openingHours) parts.push(`🕐 ${poi.openingHours}`)

  return parts.join('\n')
}

function isNearExistingStop(poi: POI): boolean {
  return props.stops.some(stop => distanceBetween(stop.lat, stop.lng, poi.lat, poi.lng) < 150)
}

function isSettlementPOI(poi: POI): boolean {
  return poi.category === 'settlement'
    || poi.type === 'city'
    || poi.type === 'town'
    || poi.type === 'village'
    || poi.type === 'hamlet'
    || poi.type === 'suburb'
    || poi.type === 'municipality'
}

function getSettlementLabelPriority(poi: POI): number {
  if (poi.type === 'city') return 0
  if (poi.type === 'town') return 1
  if (poi.type === 'village') return 2
  if (poi.type === 'municipality') return 3
  if (poi.type === 'suburb') return 4
  return 5
}

function getPOIDisplayName(poi: POI): string {
  if (mapLanguage.value === 'en' && poi.tags['name:en']) {
    return poi.tags['name:en']
  }

  return poi.name || poi.tags.name || 'Unnamed'
}

function normalizePOIStopType(poi: POI): StopType {
  if (isSettlementPOI(poi)) return 'city'

  if (
    poi.type === 'gas_station'
    || poi.type === 'ev_charging'
    || poi.type === 'rest_area'
    || poi.type === 'restaurant'
    || poi.type === 'fast_food'
    || poi.type === 'cafe'
    || poi.type === 'hotel'
    || poi.type === 'motel'
    || poi.type === 'camping'
    || poi.type === 'museum'
    || poi.type === 'park'
    || poi.type === 'viewpoint'
    || poi.type === 'shopping'
    || poi.type === 'parking'
    || poi.type === 'attraction'
  ) {
    return poi.type
  }

  if (poi.category === 'attraction') {
    return 'attraction'
  }

  return 'other'
}

function isSettlementType(type?: string): boolean {
  return type === 'city' || type === 'town' || type === 'village' || type === 'hamlet' || type === 'municipality' || type === 'place' || type === 'locality'
}

function getMapLocationIcon(place: ReverseGeocodingPlace): string {
  return isSettlementType(place.type) ? '🏘️' : '📍'
}

function isFeaturedPOI(poi: POI): boolean {
  return isHighwayRelatedPOI(poi)
    || poi.category === 'attraction'
    || poi.type === 'museum'
    || poi.type === 'viewpoint'
    || poi.type === 'park'
}

function getRoutePath(): { lat: number; lng: number }[] {
  if (routeCoordinates.length >= 2) {
    return routeCoordinates
  }

  return props.stops
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(stop => ({ lat: stop.lat, lng: stop.lng }))
}

function isHighwayRelatedPOI(poi: POI): boolean {
  return poi.tags.highway === 'rest_area'
    || poi.tags.highway === 'services'
    || poi.tags.highway === 'motorway_junction'
}

function getPOILabel(poi: POI): string {
  if (isSettlementPOI(poi)) return 'Település'
  if (poi.tags.highway === 'services') return 'Autópálya szolgáltató'
  if (poi.tags.highway === 'rest_area') return 'Pihenőhely'
  if (poi.tags.highway === 'motorway_junction') return 'Autópálya lehajtó'
  if (poi.category === 'attraction') return 'Nevezetesség'
  if (poi.category === 'food') return 'Étkezési lehetőség'
  if (poi.category === 'fuel') return 'Töltő / üzemanyag'
  return 'Hely'
}

function getPOITooltipText(poi: POI): string {
  const label = getPOILabel(poi)
  const displayName = getPOIDisplayName(poi)

  if (displayName && displayName !== 'Unnamed') {
    return label === 'Hely' ? displayName : `${label} · ${displayName}`
  }

  return label
}

function getPOIIcon(poi: POI): string {
  if (poi.tags.highway === 'services' || poi.tags.highway === 'rest_area' || poi.tags.highway === 'motorway_junction') {
    return '🛣️'
  }

  const icons: Record<string, string> = {
    city: '🏙️',
    town: '🏘️',
    village: '🏘️',
    hamlet: '🏘️',
    suburb: '🏘️',
    municipality: '🏘️',
    gas_station: '⛽',
    ev_charging: '⚡',
    restaurant: '🍽️',
    cafe: '☕',
    fast_food: '🍔',
    hotel: '🏨',
    motel: '🛏️',
    camping: '⛺',
    attraction: '🎯',
    museum: '🏛️',
    zoo: '🦁',
    theme_park: '🎡',
    gallery: '🖼️',
    aquarium: '🐠',
    viewpoint: '🏔️',
    parking: '🅿️',
    rest_area: '🚻',
    shopping: '🛍️',
    park: '🌳',
    other: '📍'
  }

  return icons[poi.type] || '📍'
}

function getPOIMarkerColor(poi: POI): string {
  if (poi.tags.highway === 'services' || poi.tags.highway === 'rest_area' || poi.tags.highway === 'motorway_junction') {
    return 'linear-gradient(135deg, #0f766e, #14b8a6)'
  }

  const colors: Record<string, string> = {
    city: 'linear-gradient(135deg, #0f766e, #14b8a6)',
    town: 'linear-gradient(135deg, #0f766e, #14b8a6)',
    village: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
    hamlet: 'linear-gradient(135deg, #38bdf8, #60a5fa)',
    suburb: 'linear-gradient(135deg, #38bdf8, #60a5fa)',
    municipality: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
    gas_station: 'linear-gradient(135deg, #f59e0b, #f97316)',
    ev_charging: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    restaurant: 'linear-gradient(135deg, #ef4444, #f97316)',
    cafe: 'linear-gradient(135deg, #d97706, #f59e0b)',
    fast_food: 'linear-gradient(135deg, #fb7185, #f43f5e)',
    hotel: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    motel: 'linear-gradient(135deg, #0ea5e9, #3b82f6)',
    camping: 'linear-gradient(135deg, #22c55e, #16a34a)',
    attraction: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    museum: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    zoo: 'linear-gradient(135deg, #22c55e, #14b8a6)',
    theme_park: 'linear-gradient(135deg, #f97316, #ef4444)',
    gallery: 'linear-gradient(135deg, #a855f7, #ec4899)',
    aquarium: 'linear-gradient(135deg, #06b6d4, #0ea5e9)',
    viewpoint: 'linear-gradient(135deg, #14b8a6, #0ea5e9)',
    parking: 'linear-gradient(135deg, #6b7280, #4b5563)',
    rest_area: 'linear-gradient(135deg, #10b981, #059669)',
    shopping: 'linear-gradient(135deg, #ec4899, #d946ef)',
    park: 'linear-gradient(135deg, #22c55e, #84cc16)',
    other: 'linear-gradient(135deg, #64748b, #475569)'
  }

  return colors[poi.type] || colors.other
}

function distanceBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2)

  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function getStopsSignature(stops: Stop[]): string {
  return JSON.stringify(
    stops.map(stop => ({
      id: stop.id,
      order: stop.order,
      name: stop.name,
      address: stop.address,
      type: stop.type,
      duration: stop.duration,
      lat: stop.lat,
      lng: stop.lng
    }))
  )
}

function panTo(lat: number, lng: number, zoom?: number) {
  if (map) {
    map.setView([lat, lng], zoom || map.getZoom())
  }
}

function fitToStops() {
  if (!map) return

  if (routingControl?._line && typeof routingControl._line.getBounds === 'function' && map.hasLayer(routingControl._line)) {
    map.fitBounds(routingControl._line.getBounds().pad(0.1), { padding: [50, 50] })
    return
  }

  if (props.stops.length >= 2) {
    const bounds = L.latLngBounds(props.stops.map(stop => [stop.lat, stop.lng] as [number, number]))
    map.fitBounds(bounds.pad(0.1), { padding: [50, 50] })
    return
  }

  if (props.stops.length === 1) {
    const [stop] = props.stops
    map.setView([stop.lat, stop.lng], 14)
    return
  }

  map.setView(props.center, props.zoom)
}

function fitBounds(bounds: [[number, number], [number, number]]) {
  if (map) {
    map.fitBounds(bounds)
  }
}

function getCenter() {
  if (!map) return null
  const center = map.getCenter()
  return { lat: center.lat, lng: center.lng }
}

defineExpose({
  panTo,
  fitToStops,
  fitBounds,
  getCenter
})
</script>

<style scoped>
/* Ensure the map container takes full height */
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  outline: none; /* Remove focus outline */
}

.map-container:focus {
  outline: none;
}

:deep(.leaflet-container) {
  height: 100%;
  width: 100%;
  outline: none !important;
}

:deep(.leaflet-container:focus) {
  outline: none !important;
}

:deep(.custom-stop-marker) {
  background: transparent !important;
  border: none !important;
}

:deep(.custom-poi-marker) {
  background: transparent !important;
  border: none !important;
}

:deep(.settlement-label-marker) {
  background: transparent !important;
  border: none !important;
}

:deep(.poi-label-tooltip) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
  border-radius: 9999px !important;
  color: #ffffff !important;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.28) !important;
  font-size: 11px !important;
  font-weight: 800 !important;
  letter-spacing: 0.02em !important;
  padding: 4px 8px !important;
}

:deep(.poi-label-tooltip.leaflet-tooltip-top:before) {
  border-top-color: rgba(15, 23, 42, 0.95) !important;
}

:deep(.leaflet-popup-content) {
  margin: 0;
  padding: 0;
}

:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>